import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import ContactBlock from "../../components/contact/ContactBlock";

const InstallationScreen = () => {
	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const guides = [
		{
			type: "Open Roll-Reverse Roll",
			desc: "Open roll shades offer a pleasing minimalist design. Reverse roll conceals the tube and prevents the shade from contacting window cranks and other obstacles.",
			image: "/images/in/open roll - reverse roll.png",
			pdf: "/pdf/Open Roll.pdf",
		},
		{
			type: "Square Cassette",
			desc: "Offering the customization options of the classic curved cassette in a sleek, modern, squared design.",
			image: "/images/shadeinfo/valance-options/square cassette.png",
			pdf: "/pdf/square.pdf",
		},
		{
			type: "Curved Cassette",
			desc: "Two sizes accommodate greater range of drop length possibilities and use of thicker fabrics. Fully assembled for quick and easy installation.",
			image: "/images/in/wrapped curved cassette.png",
			pdf: "/pdf/Curved Cassettes.pdf",
		},
		{
			type: "Motorization & Automation",
			desc: "Our motorization line features robust, rechargeable motors with bi-directional communication. Rechargeable motors make automating your shades easy.",
			image: "/images/in/motor.png",
			pdf: "/pdf/OCS - Specifications & Programming - OCS Rechargeable Motors.pdf",
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>
					Window Shade Installation Guide: Step-By-Step Process | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Window shade installation guides for every type. Off Cut Shades features a step-by-step process to install your custom shades and blinds."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/installation-guide"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<img
							src="/images/newHome/Installation-Guides-80x80.png"
							alt="Installation Guide"
							className="w-20 h-20"
						/>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Installation{" "}
						<span className="relative">
							<span className="text-primary">Guides</span>
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-600 max-w-3xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.4 }}
					>
						Our easy-to-follow instructions will walk you through the
						installation process step-by-step. In most cases, you will only need
						4 screws (included) and a screwdriver to install your new shades.
					</motion.p>
				</motion.div>

				{/* Installation Guides Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
					{guides.map((guide, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
							whileHover={{ y: -5 }}
						>
							<div className="p-8">
								<div className="flex justify-center mb-6">
									<div className="w-32 h-32 bg-gray-50 rounded-2xl p-4 flex items-center justify-center">
										<img
											src={guide.image}
											alt={guide.type}
											className="w-full h-full object-contain"
										/>
									</div>
								</div>

								<h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
									{guide.type}
								</h2>
								<p className="text-gray-600 text-center mb-6">{guide.desc}</p>

								<div className="flex justify-center">
									<motion.a
										href={guide.pdf}
										target="_blank"
										className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-xl font-semibold hover:bg-primary hover:text-white transition-colors duration-200"
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.95 }}
									>
										<Download size={20} />
										<span>Download PDF</span>
									</motion.a>
								</div>
							</div>
						</motion.div>
					))}
				</div>

				{/* Support Section */}
				{/* <motion.div
					className="mt-24 bg-gradient-to-r from-secondary to-primary rounded-3xl p-12 text-white text-center relative overflow-hidden"
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
					viewport={{ once: true }}
				>
					<motion.div
						className="absolute -top-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"
						animate={{
							scale: [1, 1.2, 1],
							rotate: [0, 180, 0],
						}}
						transition={{
							duration: 8,
							repeat: Infinity,
							ease: "linear",
						}}
					/>
					<motion.div
						className="absolute -bottom-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"
						animate={{
							scale: [1.2, 1, 1.2],
							rotate: [180, 0, 180],
						}}
						transition={{
							duration: 8,
							repeat: Infinity,
							ease: "linear",
						}}
					/>

					<h2 className="text-3xl font-bold mb-4 relative z-10">
						Need Installation Help?
					</h2>
					<p className="text-xl mb-8 relative z-10">
						Our support team is here to assist you with any questions during the
						installation process.
					</p>
					<motion.button
						className="inline-flex items-center px-8 py-4 bg-white text-primary rounded-xl font-semibold shadow-lg"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						Contact Support
					</motion.button>
				</motion.div> */}
			</main>

			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default InstallationScreen;
